'use client';

import type { CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import { Beam, rem } from './SeasonalLayers';
import styles from './seasonal.module.css';

/**
 * Background of the Black Month promo popup, layer by layer from the Figma
 * frames "BLACK MONTH · popup afiliado desktop / mobile" (12770:42014,
 * 971x552 and 12770:42070, 400x586 of "IQ Capital | New UI 2026"). The
 * designer replaced the statue after the handoff, so the handoff's flattened
 * `arte@2x` plates in public/previews/popups-handoff/bm-popup-* no longer
 * match and are not used. The countdown variant of the popup (its frames
 * were deleted from Figma) shares this scene.
 *
 * Three kinds of layer, bottom to top:
 *  - `base-*.webp`: everything below the silver light, rasterised once at 2x
 *    in Chromium from the frame's own CSS — the Popup gradient, the 10%
 *    exclusion grain, the 7% light beams, the blurred vignette, the 11%
 *    luminosity / colour-dodge fresco and the 82% obsidian darkening (plus
 *    the lighten ellipse on mobile). All static, so one image replaces a
 *    dozen blended layers and ~20MB of source textures.
 *  - the silver light ("luz prata": the frame's image fill screened over the
 *    card at 50% on desktop, 100% on mobile), drawn by the handoff's swaying
 *    Beam so it keeps moving; and the statue, its own <img> at the Figma box.
 *  - the blurred edge strips above the statue, in CSS with the frame's values.
 *
 * Sizes are Figma px / 8 (1rem = 8px at the design width). The backdrop
 * blurs stay in px like the countdown boxes' — they do not scale with rem.
 */

const BASE = '/seasonal/black-month/popup';

/** Figma box of the statue: desktop (-156, -207) 734x772.783 (12770:42063), mobile (-35, -49) 392.432x308 (12770:42094). */
const STATUE: Record<'desktop' | 'mobile', CSSProperties> = {
  desktop: { left: rem(-156), top: rem(-207), width: rem(734), height: rem(772.783) },
  mobile: { left: rem(-35), top: rem(-49), width: rem(392.432), height: rem(308) },
};

/** The mobile layers are 386 wide in a 384 card (12770:42071 clips them); the desktop ones fill the card. */
const BASE_BOX: Record<'desktop' | 'mobile', CSSProperties> = {
  desktop: { inset: 0, width: '100%', height: '100%' },
  mobile: { left: 0, top: 0, width: rem(386), height: rem(570) },
};

const EDGE_BLUR = 'blur(2.631px)';
/** 12770:42060 / 42087 / 42088: `bg-gradient-to-l` on a rect rotated 180° and flipped, i.e. mirrored. */
const EDGE_GRADIENT = 'linear-gradient(to right, rgba(0,16,11,0.05), rgba(0,16,11,0))';

function Layers({ variant }: { variant: 'desktop' | 'mobile' }) {
  return (
    <div className={cn('absolute inset-0', styles.artIn)}>
      {/* eslint-disable-next-line @next/next/no-img-element -- rasterised Figma layers */}
      <img
        src={`${BASE}/base-${variant}.webp`}
        alt=""
        className="absolute max-w-none object-cover"
        style={BASE_BOX[variant]}
      />
      {/* 12770:42036 / 42086 "luz prata (axial beam)": the image fill screened over the card */}
      <Beam
        src={`${BASE}/light.webp`}
        className={cn('[&_img]:object-cover', variant === 'desktop' && 'opacity-50')}
        style={BASE_BOX[variant]}
        entrance
      />
      {/* 12770:42063 / 42094 "estátua" */}
      <div className="absolute" style={STATUE[variant]} data-seasonal-layer="statue">
        {/* eslint-disable-next-line @next/next/no-img-element -- Figma image fill */}
        <img
          src={`${BASE}/statue-${variant}.webp`}
          alt=""
          className="absolute inset-0 h-full w-full max-w-none object-cover"
        />
      </div>
    </div>
  );
}

export default function BlackMonthPopupScene() {
  return (
    <>
      {/* ---------------- Mobile · 12770:42071 (384x570) ---------------- */}
      <div className="absolute inset-0 lg:hidden">
        <Layers variant="mobile" />
        {/* 12770:42087 / 42088: two blurred strips on the left edge, 12770:42089 on the right */}
        <div
          className="absolute"
          style={{ left: rem(-48), top: rem(-88), width: rem(76), height: rem(1023), backdropFilter: EDGE_BLUR, background: EDGE_GRADIENT }}
        />
        <div
          className="absolute"
          style={{ left: rem(-48), top: rem(-88), width: rem(83), height: rem(1023), backdropFilter: EDGE_BLUR, background: EDGE_GRADIENT }}
        />
        <div
          className="absolute"
          style={{
            left: rem(347),
            top: rem(-88),
            width: rem(44),
            height: rem(1023),
            backdropFilter: EDGE_BLUR,
            background: 'linear-gradient(269.236101549085deg, rgba(0,16,11,0.05) 11.832%, rgba(0,16,11,0) 76.903%)',
          }}
        />
      </div>

      {/* ---------------- Desktop · 12770:42015 (957x538) ---------------- */}
      <div className="absolute inset-0 hidden lg:block">
        <Layers variant="desktop" />
        {/* 12770:42060: blurred strip on the left edge, (-48, -88) 162x1022.538 */}
        <div
          className="absolute"
          style={{ left: rem(-48), top: rem(-88), width: rem(162), height: rem(1022.538), backdropFilter: EDGE_BLUR, background: EDGE_GRADIENT }}
        />
      </div>
    </>
  );
}
